import type { DashboardSummary } from "@excellent/core/server";

/**
 * Returns a copy of a {@link DashboardSummary} with every navigable href
 * rewritten through `remap`. Used to surface a product dashboard inside a
 * project section: the shared `productDashboardPage` computation still emits
 * standalone `/<product>/*` hrefs, and this rewrites them all to the section
 * base path in one place (was hand-inlined identically in every
 * `_<product>/dashboard-view.tsx`).
 *
 * Pure — does not mutate the input.
 */
export function remapDashboardSummary(
  summary: DashboardSummary,
  remap: (href: string) => string,
): DashboardSummary {
  // Generic so a required `string` href stays `string` and an optional one
  // stays optional — the summary's nested href fields differ in nullability.
  const r = <T extends string | undefined>(href: T): T =>
    (href ? remap(href) : href) as T;

  const rawPrimary = summary.primary;
  let primary = rawPrimary;
  if (rawPrimary) {
    const href = r(rawPrimary.href);
    primary =
      rawPrimary.kind === "table"
        ? {
            ...rawPrimary,
            href,
            rows: rawPrimary.rows.map((row) => ({
              ...row,
              _href: r(row._href),
            })) as typeof rawPrimary.rows,
          }
        : { ...rawPrimary, href };
  }

  return {
    ...summary,
    hero: summary.hero
      ? { ...summary.hero, href: r(summary.hero.href) }
      : summary.hero,
    stats: summary.stats.map((s) => ({ ...s, href: r(s.href) })),
    attention: summary.attention.map((a) => ({ ...a, href: r(a.href) })),
    insights: summary.insights.map((i) => ({ ...i, href: r(i.href) })),
    recentActivityGroups: summary.recentActivityGroups.map((g) => ({
      ...g,
      href: r(g.href),
      members: g.members.map((m) => ({ ...m, href: r(m.href) })),
    })),
    primary,
  };
}
